import React, { useState, useEffect } from 'react';
import { Search, Plus, X, Building2 } from 'lucide-react';
import useClickOutside from '../hooks/useClickOutside.js';

const MicInwardDetails = ({
  formData,
  setFormData,
  allCustomers,
  allVendors,
  allWarehouses,
  selectedCustomer,
  setSelectedCustomer,
  selectedVendor,
  setSelectedVendor,
  selectedSourceWarehouse,
  setSelectedSourceWarehouse,
  selectedWarehouse,
  setSelectedWarehouse,
  setShowNewCustomerModal,
  setShowNewVendorModal,
  setShowNewWarehouseModal,
  setShowAddAddressModal,
  setAddressModalType,
  setAddressDataToEdit,
  sectionTitle = "Consignee & Pickup Details",
  validationErrors = {},
  setValidationErrors
}) => {
  const typeOfDoc = formData.typeOfMIC;

  const clearValidationError = (fieldName) => {
    if (!setValidationErrors) return;
    setValidationErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const getFieldStyle = (fieldName, baseStyle = {}) => ({
    ...baseStyle,
    ...(validationErrors[fieldName] ? errorInputStyle : {})
  });

  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const customerSearchRef = useClickOutside(() => setShowCustomerSuggestions(false));

  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [showVendorSuggestions, setShowVendorSuggestions] = useState(false);
  const vendorSearchRef = useClickOutside(() => setShowVendorSuggestions(false));

  const [sourceWarehouseSearchTerm, setSourceWarehouseSearchTerm] = useState('');
  const [filteredSourceWarehouses, setFilteredSourceWarehouses] = useState([]);
  const [showSourceWarehouseSuggestions, setShowSourceWarehouseSuggestions] = useState(false);
  const sourceWarehouseSearchRef = useClickOutside(() => setShowSourceWarehouseSuggestions(false));

  const [warehouseSearchTerm, setWarehouseSearchTerm] = useState('');
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [showWarehouseSuggestions, setShowWarehouseSuggestions] = useState(false);
  const warehouseSearchRef = useClickOutside(() => setShowWarehouseSuggestions(false));

  // Sync terms with selected objects
  useEffect(() => {
    if (selectedCustomer) setCustomerSearchTerm(selectedCustomer.display_name || '');
    else if (typeOfDoc === 'Sale Return' || typeOfDoc === 'Rental Return') setCustomerSearchTerm(formData.customerName || '');
    else setCustomerSearchTerm('');
  }, [selectedCustomer, formData.customerName, typeOfDoc]);

  useEffect(() => {
    if (selectedVendor) setVendorSearchTerm(selectedVendor.name || '');
    else if (typeOfDoc === 'Purchase') setVendorSearchTerm(formData.customerName || '');
    else setVendorSearchTerm('');
  }, [selectedVendor, formData.customerName, typeOfDoc]);

  useEffect(() => {
    if (selectedSourceWarehouse) setSourceWarehouseSearchTerm(selectedSourceWarehouse.name || '');
    else if (typeOfDoc === 'Interbranch Transfer') setSourceWarehouseSearchTerm(formData.customerName || '');
    else setSourceWarehouseSearchTerm('');
  }, [selectedSourceWarehouse, formData.customerName, typeOfDoc]);

  useEffect(() => {
    if (selectedWarehouse) setWarehouseSearchTerm(selectedWarehouse.name || '');
    else setWarehouseSearchTerm(formData.warehouse || '');
  }, [selectedWarehouse, formData.warehouse]);

  const formatAddress = (addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    return `${addr.attention ? addr.attention + ', ' : ''}${addr.address || ''}, ${addr.city || ''}, ${addr.state || ''} - ${addr.pincode || ''}`;
  };

  const handleCustomerSearch = (value) => {
    setCustomerSearchTerm(value);
    if (value.length > 0) {
      const term = value.toLowerCase();
      const matches = allCustomers.filter(c => c.display_name?.toLowerCase().includes(term));
      setFilteredCustomers(matches);
      setShowCustomerSuggestions(true);
    } else {
      setFilteredCustomers(allCustomers);
      setShowCustomerSuggestions(true);
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearchTerm(customer.display_name);
    setShowCustomerSuggestions(false);
    clearValidationError('customerName');
    const firstShipping = customer.addresses?.find(a => a.address_type === 'Shipping');
    setFormData(prev => ({ 
        ...prev, 
        customerName: customer.display_name, 
        customerID: customer.id,
        vendorID: '',
        sourceWarehouseID: '',
        shippingAddress: firstShipping ? formatAddress(firstShipping) : ''
    }));
  };

  const handleVendorSearch = (value) => {
    setVendorSearchTerm(value);
    if (value.length > 0) {
      const term = value.toLowerCase();
      const matches = allVendors.filter(v => 
        v.name?.toLowerCase().includes(term) || 
        v.code?.toLowerCase().includes(term)
      );
      setFilteredVendors(matches);
      setShowVendorSuggestions(true);
    } else {
      setFilteredVendors(allVendors);
      setShowVendorSuggestions(true);
    }
  };

  const selectVendor = (vendor) => {
    setSelectedVendor(vendor);
    setVendorSearchTerm(vendor.name);
    setShowVendorSuggestions(false);
    setFormData(prev => ({ 
        ...prev, 
        customerName: vendor.name, 
        vendorID: vendor.id,
        customerID: '',
        sourceWarehouseID: '',
        shippingAddress: vendor.address ? `${vendor.address}, ${vendor.city || ''}, ${vendor.state || ''} - ${vendor.pincode || ''}` : ''
    }));
  };

  const handleSourceWarehouseSearch = (value) => {
    setSourceWarehouseSearchTerm(value);
    if (value.length > 0) {
      const term = value.toLowerCase();
      const matches = allWarehouses.filter(w => 
        w.name?.toLowerCase().includes(term) || 
        w.code?.toLowerCase().includes(term)
      );
      setFilteredSourceWarehouses(matches);
      setShowSourceWarehouseSuggestions(true);
    } else {
      setFilteredSourceWarehouses(allWarehouses);
      setShowSourceWarehouseSuggestions(true);
    }
  };

  const selectSourceWarehouse = (warehouse) => {
    setSelectedSourceWarehouse(warehouse);
    setSourceWarehouseSearchTerm(warehouse.name);
    setShowSourceWarehouseSuggestions(false);
    setFormData(prev => ({ 
        ...prev, 
        customerName: warehouse.name, 
        sourceWarehouseID: warehouse.id,
        customerID: '',
        vendorID: '',
        shippingAddress: warehouse.address ? `${warehouse.address}, ${warehouse.city || ''}, ${warehouse.state || ''} - ${warehouse.pincode || ''}` : ''
    }));
  };

  const handleWarehouseSearch = (value) => {
    setWarehouseSearchTerm(value);
    if (value.length > 0) {
      const term = value.toLowerCase();
      const matches = allWarehouses.filter(w => 
        w.name?.toLowerCase().includes(term) || 
        w.code?.toLowerCase().includes(term)
      );
      setFilteredWarehouses(matches);
      setShowWarehouseSuggestions(true);
    } else {
      setFilteredWarehouses(allWarehouses);
      setShowWarehouseSuggestions(true);
    }
  };

  const selectDestWarehouse = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setWarehouseSearchTerm(warehouse.name);
    clearValidationError('warehouse');
    setFormData(prev => ({
      ...prev,
      warehouse: warehouse.name
    }));
    setShowWarehouseSuggestions(false);
  };

  const handleAddressSelect = (addressId) => {
    if (addressId === 'new') {
      setAddressDataToEdit(null);
      setShowAddAddressModal(true);
    } else {
      const address = selectedCustomer.addresses?.find(a => a.id === addressId);
      if (address) {
        setFormData(prev => ({ 
          ...prev, 
          shippingAddress: formatAddress(address)
        }));
      }
    }
  };

  const isReturn = typeOfDoc === 'Sale Return' || typeOfDoc === 'Rental Return';
  const isPurchase = typeOfDoc === 'Purchase';
  const isInterbranch = typeOfDoc === 'Interbranch Transfer';

  return (
    <div style={cardStyle}>
      <h3 style={sectionHeaderStyle}>{sectionTitle}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
        
        {/* SOURCE COLUMN */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div style={{ fontSize: '12px', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase' }}>
            Source ({isPurchase ? 'Vendor' : isInterbranch ? 'Source Warehouse' : 'Pickup From'})
          </div>
          
          {isPurchase ? (
            <div style={{ position: 'relative' }} ref={vendorSearchRef}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '6px' }}>
                <label style={labelStyle}>Vendor Name</label>
                <button onClick={() => setShowNewVendorModal(true)} style={linkBtnStyle}>
                  <Plus size={14} /> New Vendor
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' }}>
                <Search size={18} color="#64748b" />
                <input 
                  type="text" 
                  placeholder="Search vendor..." 
                  value={vendorSearchTerm} 
                  onChange={(e) => handleVendorSearch(e.target.value)} 
                  onFocus={() => (vendorSearchTerm.length > 0 || filteredVendors.length > 0) && setShowVendorSuggestions(true)}
                  style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px' }}
                />
                {vendorSearchTerm && (
                  <button onClick={() => { setSelectedVendor(null); setVendorSearchTerm(''); setFormData(prev => ({...prev, customerName: '', vendorID: '', shippingAddress: ''})); }} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                    <X size={16} color="#64748b" />
                  </button>
                )}
              </div>
              {showVendorSuggestions && (
                <div style={suggestionsBoxStyle}>
                  {filteredVendors.length > 0 ? (
                    filteredVendors.map(ven => (
                      <div key={ven.id} onClick={() => selectVendor(ven)} style={suggestionItemStyle}>
                        <div style={{ fontWeight: 'bold' }}>{ven.name} ({ven.code})</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{ven.city}, {ven.state}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#64748b' }}>No vendors found</div>
                  )}
                </div>
              )}
            </div>
          ) : isInterbranch ? (
            <div style={{ position: 'relative' }} ref={sourceWarehouseSearchRef}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '6px' }}>
                <label style={labelStyle}>Source Warehouse</label>
                <button onClick={() => setShowNewWarehouseModal(true)} style={linkBtnStyle}>
                  <Plus size={14} /> New Warehouse
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' }}>
                <Building2 size={18} color="#64748b" />
                <input 
                  type="text" 
                  placeholder="Search warehouse..." 
                  value={sourceWarehouseSearchTerm} 
                  onChange={(e) => handleSourceWarehouseSearch(e.target.value)} 
                  onFocus={() => (sourceWarehouseSearchTerm.length > 0 || filteredSourceWarehouses.length > 0) && setShowSourceWarehouseSuggestions(true)}
                  style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px' }}
                />
                {sourceWarehouseSearchTerm && (
                  <button onClick={() => { setSelectedSourceWarehouse(null); setSourceWarehouseSearchTerm(''); setFormData(prev => ({...prev, customerName: '', sourceWarehouseID: '', shippingAddress: ''})); }} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                    <X size={16} color="#64748b" />
                  </button>
                )}
              </div>
              {showSourceWarehouseSuggestions && (
                <div style={suggestionsBoxStyle}>
                  {filteredSourceWarehouses.length > 0 ? (
                    filteredSourceWarehouses.map(wh => (
                      <div key={wh.id} onClick={() => selectSourceWarehouse(wh)} style={suggestionItemStyle}>
                        <div style={{ fontWeight: 'bold' }}>{wh.name} ({wh.code})</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{wh.city}, {wh.state}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#64748b' }}>No warehouses found</div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative' }} ref={customerSearchRef}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '6px' }}>
                <label style={labelStyle}>Consignee (Customer Name)</label>
                <button onClick={() => setShowNewCustomerModal(true)} style={linkBtnStyle}>
                  <Plus size={14} /> New Customer
                </button>
              </div>
              <div style={getFieldStyle('customerName', { display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' })}>
                <Search size={18} color="#64748b" />
                <input 
                  type="text" 
                  placeholder="Search customer..." 
                  value={customerSearchTerm} 
                  onChange={(e) => { clearValidationError('customerName'); handleCustomerSearch(e.target.value); }} 
                  onFocus={() => (customerSearchTerm.length > 0 || filteredCustomers.length > 0) && setShowCustomerSuggestions(true)}
                  style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px' }}
                />
                {customerSearchTerm && (
                  <button onClick={() => { setSelectedCustomer(null); setCustomerSearchTerm(''); setFormData(prev => ({...prev, customerName: '', customerID: '', shippingAddress: ''})); }} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                    <X size={16} color="#64748b" />
                  </button>
                )}
              </div>
              {showCustomerSuggestions && (
                <div style={suggestionsBoxStyle}>
                  {filteredCustomers.length > 0 ? (
                    filteredCustomers.map(cust => (
                      <div key={cust.id} onClick={() => selectCustomer(cust)} style={suggestionItemStyle}>
                        <div style={{ fontWeight: 'bold' }}>{cust.display_name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{cust.company_name}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#64748b' }}>No customers found</div>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '6px' }}>
              <label style={labelStyle}>
                {isPurchase ? 'Vendor Address' : isInterbranch ? 'Source Warehouse Address' : 'Pickup From (Shipping Address)'}
              </label>
              {isReturn && selectedCustomer && (
                <button 
                  onClick={() => { setAddressModalType('shipping'); setAddressDataToEdit(null); setShowAddAddressModal(true); }} 
                  style={linkBtnStyle}
                >
                  <Plus size={14} /> New Address
                </button>
              )}
            </div>
            
            {isReturn && selectedCustomer && (
              <select 
                onChange={(e) => handleAddressSelect(e.target.value)} 
                style={{ ...inputStyle, marginBottom: '8px' }}
                value={selectedCustomer.addresses?.find(a => formatAddress(a) === formData.shippingAddress)?.id || ''}
              >
                <option value="">Select Shipping Address...</option>
                {selectedCustomer.addresses?.filter(a => a.address_type === 'Shipping').map(addr => (
                  <option key={addr.id} value={addr.id}>{formatAddress(addr)}</option>
                ))}
                <option value="new">+ Add New Address</option>
              </select>
            )}

            <div style={{ ...inputStyle, minHeight: '80px', backgroundColor: '#f9fafb', color: '#475569', fontSize: '13px', overflowY: 'auto' }}>
              {isPurchase ? (
                selectedVendor ? (
                  formatAddress(selectedVendor)
                ) : (
                  <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Select a vendor to view address...</span>
                )
              ) : isInterbranch ? (
                selectedSourceWarehouse ? (
                  <>
                    {selectedSourceWarehouse.address}<br />
                    {selectedSourceWarehouse.city}, {selectedSourceWarehouse.state} - {selectedSourceWarehouse.pincode}
                  </>
                ) : (
                  <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Select a source warehouse to view address...</span>
                )
              ) : (
                <textarea 
                  name="shippingAddress" 
                  value={formData.shippingAddress} 
                  readOnly
                  placeholder={selectedCustomer || selectedVendor || selectedSourceWarehouse ? "Selected address details..." : "Search source to select an address..."}
                  style={{ width: '100%', height: '100%', border: 'none', background: 'transparent', outline: 'none', resize: 'none', fontFamily: 'inherit' }} 
                />
              )}
            </div>
          </div>

          <div style={{ backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', minHeight: '80px' }}>
            <label style={labelStyle}>Source Contact Details</label>
            {isPurchase ? (
              selectedVendor ? (
                <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ fontWeight: '600', color: '#1e293b' }}>{selectedVendor.contactPerson || '—'}</div>
                  <div style={{ color: '#475569' }}>Phone: {selectedVendor.phone || '—'}</div>
                  <div style={{ color: '#475569' }}>Email: {selectedVendor.email || '—'}</div>
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>Select vendor to see details...</div>
              )
            ) : isInterbranch ? (
              selectedSourceWarehouse ? (
                <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ fontWeight: '600', color: '#1e293b' }}>{selectedSourceWarehouse.contactPerson || '—'}</div>
                  <div style={{ color: '#475569' }}>Phone: {selectedSourceWarehouse.phone || '—'}</div>
                  <div style={{ color: '#475569' }}>Email: {selectedSourceWarehouse.email || '—'}</div>
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>Select source warehouse to see details...</div>
              )
            ) : (
              selectedCustomer ? (
                <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ fontWeight: '600', color: '#1e293b' }}>
                    {selectedCustomer.primary_contact?.salutation} {selectedCustomer.primary_contact?.first_name} {selectedCustomer.primary_contact?.last_name}
                  </div>
                  <div style={{ color: '#475569' }}>Phone: {selectedCustomer.mobile || selectedCustomer.work_phone || '—'}</div>
                  <div style={{ color: '#475569' }}>Email: {selectedCustomer.email || '—'}</div>
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>Select customer to see details...</div>
              )
            )}
          </div>
        </div>

        {/* DESTINATION COLUMN (ALWAYS WAREHOUSE) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div style={{ fontSize: '12px', fontWeight: '800', color: '#059669', textTransform: 'uppercase' }}>
            Destination (Our Warehouse)
          </div>

          <div style={{ position: 'relative' }} ref={warehouseSearchRef}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '6px' }}>
              <label style={labelStyle}>Warehouse</label>
              <button onClick={() => setShowNewWarehouseModal(true)} style={linkBtnStyle}>
                <Plus size={14} /> New Warehouse
              </button>
            </div>
            <div style={getFieldStyle('warehouse', { display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' })}>
              <Building2 size={18} color="#64748b" />
              <input 
                type="text" 
                placeholder="Search warehouse..." 
                value={warehouseSearchTerm} 
                onChange={(e) => { clearValidationError('warehouse'); handleWarehouseSearch(e.target.value); }} 
                onFocus={() => (warehouseSearchTerm.length > 0 || filteredWarehouses.length > 0) && setShowWarehouseSuggestions(true)}
                style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px' }}
              />
              {warehouseSearchTerm && (
                <button onClick={() => { setSelectedWarehouse(null); setWarehouseSearchTerm(''); setFormData(prev => ({...prev, warehouse: ''})); }} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                  <X size={16} color="#64748b" />
                </button>
              )}
            </div>
            {showWarehouseSuggestions && (
              <div style={suggestionsBoxStyle}>
                {filteredWarehouses.length > 0 ? (
                  filteredWarehouses.map(wh => (
                    <div key={wh.id} onClick={() => selectDestWarehouse(wh)} style={suggestionItemStyle}>
                      <div style={{ fontWeight: 'bold' }}>{wh.name} ({wh.code})</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{wh.city}, {wh.state}</div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '10px', textAlign: 'center', color: '#64748b' }}>No warehouses found</div>
                )}
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle}>Warehouse Address</label>
            <div style={{ ...inputStyle, minHeight: '80px', backgroundColor: '#f9fafb', color: '#374151', fontSize: '13px', overflowY: 'auto' }}>
              {selectedWarehouse ? (
                <>
                  {selectedWarehouse.address}<br />
                  {selectedWarehouse.city}, {selectedWarehouse.state} - {selectedWarehouse.pincode}
                </>
              ) : (
                <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Select a warehouse to view address...</span>
              )}
            </div>
          </div>

          <div style={{ backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '8px', border: '1px solid #dcfce7', minHeight: '80px' }}>
            <label style={{ ...labelStyle, color: '#166534' }}>Destination Contact Details</label>
            {selectedWarehouse ? (
              <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ fontWeight: '600', color: '#14532d' }}>{selectedWarehouse.contactPerson || '—'}</div>
                <div style={{ color: '#166534' }}>Phone: {selectedWarehouse.phone || '—'}</div>
                <div style={{ color: '#166534' }}>Email: {selectedWarehouse.email || '—'}</div>
              </div>
            ) : (
              <div style={{ fontSize: '13px', color: '#86efac', fontStyle: 'italic' }}>Select warehouse to see details...</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '10px', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const suggestionsBoxStyle = { position: 'absolute', top: '100%', left: 0, width: '100%', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '4px', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' };
const suggestionItemStyle = { padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' };
const linkBtnStyle = { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: 4, padding: 0 };
const errorInputStyle = { border: '1px solid #dc2626', boxShadow: '0 0 0 1px #dc2626', borderRadius: '6px' };

export default MicInwardDetails;
